import type { Dispatch } from "react";
import AcceleratorDoc from "../../models/AcceleratorDoc";
import { Hydratable } from "../../models/hydration/Hydratable";
import { DlfMatcher } from "../../models/matchers/DlfMatcher";
import { MatcherFactory } from "../../models/matchers/factory/MatcherFactory";
import { Matcher } from "../../models/matchers/Matcher";
import { DlfExtractor } from "../../services/extraction/DlfExtractor";
import { DlfMatcherTypes } from "../../types/rules/DlfMatcherTypes";
import { useAsyncReducer } from "./useAsyncReducer";
import { BaseField } from "../../models/extraction/BaseField";
import { LocationMatcher } from "../../models/matchers/LocationMatcher";

export { useClassificationRuleHighlights };
/**
 * Type for storing Span Ids for Matcher. First item is matcherId, second is list of Span IDs. 3rd is Page Index.
 */
type HighlightState = [string, string[], number][];

/**
 * Type of Reducer action
 */
type HighlightAction = {
  type: "add" | "remove" | "set";
  data: [string, string[] | null, number][];
};

function ruleReducer(
  state: HighlightState,
  action: HighlightAction,
): HighlightState {
  if (action.type === "remove") {
    // remove any data[0] matcherId from state
    return state.filter((x) => action.data.find((d) => d[0] !== x[0]));
  } else if (action.type === "add") {
    // merge state with new data
    return action.data
      .filter((value): value is [string, string[], number] => value[1] != null)
      .concat(state)
      .filter((x, i, arr) => arr.findIndex((a) => a[0] === x[0]) === i);
  } else if (action.type === "set") {
    return action.data.filter(
      (value): value is [string, string[], number] => value[1] != null,
    );
  }
  return state;
}

function useClassificationRuleHighlights(
  acceleratorDoc: Hydratable<AcceleratorDoc> | undefined,
  matchers: Matcher[],
  documentStale: () => void,
): [HighlightState, VoidFunction, boolean, Dispatch<HighlightAction>] {
  const extractMatchers = async (
    state: HighlightState,
  ): Promise<HighlightAction> => {
    if (
      matchers.length === 0 ||
      !acceleratorDoc ||
      !acceleratorDoc.isHydrated(false)
    ) {
      return {
        type: "set",
        data: [],
      };
    }

    const extractedAlready: string[] = [];
    const extractData: [Matcher, DlfMatcher][] = matchers
      // filter out empty start rules
      // remove matchers that have already been extracted
      .filter((matcher) => !extractedAlready.includes(matcher.getMatcherId()))
      // TODO: extract into explicit MatcherFactory methods
      // convert to Matcher
      .map((x) => {
        const dlfMatcherProps: any = {
          ...x,
          threshold: x.threshold,
          ocrConfidenceThreshold: -1,
          discriminator: (x.getDiscriminator() === "M_LOCATION"
            ? "F_SECTION"
            : x.getDiscriminator().replace("M_", "F_")) as DlfMatcherTypes,
          fieldName: "",
          position: 0,
          reusable: true,
        };

        if (x.getDiscriminator() === "M_LOCATION") {
          const loc = x as LocationMatcher;
          dlfMatcherProps.matcher = {
            ...loc.matcher,
            discriminator: loc.matcher
              .getDiscriminator()
              .replace("M_", "F_") as DlfMatcherTypes,
          };
        }

        return [x, MatcherFactory.createDlfMatcher(dlfMatcherProps)] as [
          Matcher,
          DlfMatcher,
        ];
      });

    let startRuleBaseFields: Array<BaseField | undefined> = [];
    try {
      startRuleBaseFields = await Promise.all(
        extractData.map((x) =>
          // this is a live preview of classification rule matches, re-run on
          // every model change (including the current user's own unsaved
          // edits), so a 404 here doesn't mean the batch changed externally
          new DlfExtractor(x[1], false).extract(acceleratorDoc.getHydrated()),
        ),
      );
    } catch (e) {
      documentStale();
      throw e;
    }

    if (startRuleBaseFields.filter((x) => x === undefined).length > 0) {
      documentStale();
      throw new Error();
    }

    const addData: [string, string[], number][] = [];
    for (let i = 0; i < startRuleBaseFields.length; ++i) {
      const baseField = startRuleBaseFields[i];
      if (baseField != null && baseField.spanIds.length) {
        const pageIndex = acceleratorDoc
          .getHydrated()
          .getPageIndex(baseField.pageId);
        addData.push([
          extractData[i][0].getMatcherId(),
          baseField.spanIds,
          pageIndex,
        ]);
      } else {
        addData.push([extractData[i][0].getMatcherId(), [], -1]);
      }
    }
    return {
      type: "set",
      data: addData,
    };
  };

  const [highlights, trigger, loading, dispatch] = useAsyncReducer(
    extractMatchers,
    ruleReducer,
    [],
  );

  return [highlights, trigger, loading, dispatch];
}
